/** Spark bar chart for compact time-series. Uses {@link getChartColorForSvg} for SVG fill. */

import { forwardRef } from "react";
import {
  Bar,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import { cn } from "@/lib/utils";
import {
  AvailableChartColors,
  type AvailableChartColorsKeys,
  constructCategoryColors,
  getChartColorForSvg,
  getYAxisDomain,
  getYAxisDomainForSeriesData,
} from "@/lib/utils/chartUtils";
import type { SparkBarChartProps } from "./SparkChart.types";

export const SparkBarChart = forwardRef<HTMLDivElement, SparkBarChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      autoMinValue = false,
      minValue,
      maxValue,
      barCategoryGap,
      type = "default",
      className,
      ...other
    } = props;

    const categoryColors = constructCategoryColors(categories, colors);
    const yAxisDomain =
      minValue != null || maxValue != null
        ? getYAxisDomain(autoMinValue, minValue, maxValue)
        : getYAxisDomainForSeriesData(data, categories, { autoMinValue });
    const stacked = type === "stacked" || type === "percent";

    return (
      <div
        ref={forwardedRef}
        aria-hidden
        className={cn("h-12 w-28", className)}
        {...other}
      >
        <ResponsiveContainer>
          <RechartsBarChart
            data={data}
            margin={{
              bottom: 1,
              left: 1,
              right: 1,
              top: 1,
            }}
            stackOffset={type === "percent" ? "expand" : undefined}
            barCategoryGap={barCategoryGap}
          >
            <XAxis hide dataKey={index} />
            <YAxis hide domain={yAxisDomain as AxisDomain} />
            {categories.map((category) => {
              const colorKey = categoryColors.get(category) as
                | AvailableChartColorsKeys
                | undefined;
              const color = getChartColorForSvg(colorKey ?? "gray");
              return (
                <Bar
                  key={category}
                  name={category}
                  type="linear"
                  dataKey={category}
                  stackId={stacked ? "stack" : undefined}
                  isAnimationActive={false}
                  fill={color}
                />
              );
            })}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

SparkBarChart.displayName = "SparkBarChart";
